/**
 * RTK Query endpoints for bulk operations — served by @cluster/communication.
 * Base path after the dev proxy + nginx rewrite strips `/api/communication`:
 * the controller prefix is `/bulk-operations`, mirroring the webhook module.
 */
import { apiSlice } from './apiSlice';
import type {
  BulkOperation,
  BulkOperationStatus,
  BulkOperationStatusDetail,
  BulkAction,
  BulkTargetEvent,
  BulkListenCompletion,
} from '@/types';

const LIST = { type: 'BulkOperation' as const, id: 'LIST' };

export interface BulkOperationListParams {
  status?: BulkOperationStatus;
  projectId?: string;
  page?: number;
  limit?: number;
}

export interface BulkChildState {
  index: number;
  jobId?: string;
  status: string;
  result?: unknown;
  error?: string;
}

function data<T>(response: { data: T }): T {
  return response.data;
}

/** Shape-agnostic list: backend may return `[]` or `{ data: [], pagination }`. */
function asList<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response as T[];
  const inner = (response as { data?: unknown } | undefined)?.data;
  return Array.isArray(inner) ? (inner as T[]) : [];
}

export const bulkApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getBulkOperations: builder.query<BulkOperation[], BulkOperationListParams | void>({
      query: (params) => ({
        url: '/communication/bulk-operations',
        params: params || undefined,
      }),
      transformResponse: (response: unknown) => asList<BulkOperation>(response),
      providesTags: (result) => {
        // Shape-agnostic: backend may return `[]` or `{ data: [], pagination }`.
        const list = Array.isArray(result)
          ? result
          : Array.isArray((result as { data?: unknown } | undefined)?.data)
            ? ((result as unknown as { data: BulkOperation[] }).data)
            : [];
        return [
          ...list.map(({ _id }) => ({ type: 'BulkOperation' as const, id: _id })),
          LIST,
        ];
      },
    }),

    getBulkOperation: builder.query<BulkOperation, string>({
      query: (id) => `/communication/bulk-operations/${id}`,
      transformResponse: data<BulkOperation>,
      providesTags: (_result, _error, id) => [{ type: 'BulkOperation', id }],
    }),

    createBulkOperation: builder.mutation<
      BulkOperation,
      Partial<BulkOperation> & {
        name: string;
        actions: BulkAction[];
        targetEvents?: BulkTargetEvent[];
        /** Required (backend fail-fast) when requiresApproval is true (BULK-2). */
        projectId?: string;
        /** Required (non-empty) when requiresApproval is true — scope of approval + run. */
        policyIds?: string[];
      }
    >({
      query: (body) => ({ url: '/communication/bulk-operations', method: 'POST', body }),
      transformResponse: data<BulkOperation>,
      invalidatesTags: [LIST],
    }),

    updateBulkOperation: builder.mutation<
      BulkOperation,
      { id: string; body: Partial<BulkOperation> }
    >({
      query: ({ id, body }) => ({
        url: `/communication/bulk-operations/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: data<BulkOperation>,
      invalidatesTags: (result, _error, { id }) =>
        result ? [{ type: 'BulkOperation', id }, LIST] : [LIST],
    }),

    deleteBulkOperation: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/communication/bulk-operations/${id}`, method: 'DELETE' }),
      invalidatesTags: [LIST],
    }),

    runBulkOperation: builder.mutation<BulkOperation | { approvalId?: string }, string>({
      query: (id) => ({ url: `/communication/bulk-operations/${id}/run`, method: 'POST' }),
      transformResponse: data<BulkOperation | { approvalId?: string }>,
      invalidatesTags: (result) =>
        result ? [{ type: 'BulkOperation', id: (result as { _id?: string })._id }, LIST] : [LIST],
    }),

    cancelBulkOperation: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/communication/bulk-operations/${id}/cancel`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'BulkOperation', id }, LIST],
    }),

    /** GET /communication/bulk-operations/:id/status — live child states + outputs. */
    getBulkOperationStatus: builder.query<BulkOperationStatusDetail, string>({
      query: (id) => `/communication/bulk-operations/${id}/status`,
      providesTags: (_result, _error, id) => [{ type: 'BulkOperation', id }],
    }),

    /** POST /communication/bulk-operations/:id/listen — register listen mode (BULK-4). */
    listenBulkOperation: builder.mutation<
      { listener: { webhookId: string; token: string } },
      { id: string; eventFilter: { channel: string; eventTypes: string[] }[]; completion?: BulkListenCompletion }
    >({
      query: ({ id, ...body }) => ({
        url: `/communication/bulk-operations/${id}/listen`,
        method: 'POST',
        body,
      }),
      transformResponse: data<{ listener: { webhookId: string; token: string } }>,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'BulkOperation', id }],
    }),
  }),
});

// Export auto-generated hooks.
export const {
  useGetBulkOperationsQuery,
  useGetBulkOperationQuery,
  useCreateBulkOperationMutation,
  useUpdateBulkOperationMutation,
  useDeleteBulkOperationMutation,
  useRunBulkOperationMutation,
  useCancelBulkOperationMutation,
  useGetBulkOperationStatusQuery,
  useListenBulkOperationMutation,
} = bulkApi;